/**
 * useBarberWaitTime - Calculate real-time wait time for a barber
 */

import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { subscribeToQueueByBarber, getTimeRemaining } from '../services/queueService';
import type { QueueTicket } from '../services/queueService';
import type { BarberStatus } from '../types';
import { pendingBreakSeconds } from '@/lib/breaks';

interface UseBarberWaitTimeOptions {
  barberId: string;
  branchId: string;
  enabled?: boolean;
}

interface UseBarberWaitTimeReturn {
  totalWaitMinutes: number;
  totalWaitSeconds: number;
  waitingCount: number;
  loading: boolean;
  error: string | null;
}

/**
 * Hook to calculate real-time wait time for a barber
 * Calculates based on:
 * - Services in progress (in_service): uses real remaining time from timerExpiry
 * - Services finishing: uses time until finishingAt
 * - Services waiting/notified/arrived: uses serviceDuration
 * - Descanso temporal en curso: suma los minutos que quedan hasta la vuelta
 *
 * Sobre el descanso: cuando el barbero marca un descanso temporal (comiendo,
 * gestión rápida…) sigue aceptando turnos, así que el tiempo que ve el cliente
 * tiene que incluir lo que resta de ese descanso. Si no, un cliente que coge
 * turno durante el almuerzo ve solo el tiempo de los turnos por delante y se
 * presenta dos horas antes de que le toque.
 *
 * No hace falta mostrarle al cliente que el barbero está comiendo: le basta el
 * tiempo total, que es lo que este hook devuelve.
 */
export function useBarberWaitTime({
  barberId,
  branchId,
  enabled = true,
}: UseBarberWaitTimeOptions): UseBarberWaitTimeReturn {
  const [tickets, setTickets] = useState<QueueTicket[]>([]);
  const [barberStatus, setBarberStatus] = useState<BarberStatus | null>(null);
  const [totalWaitSeconds, setTotalWaitSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to barber's queue tickets
  useEffect(() => {
    if (!enabled || !barberId || !branchId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeToQueueByBarber(
      barberId,
      branchId,
      (queueTickets) => {
        setTickets(queueTickets);
        setLoading(false);
      },
      (err) => {
        console.error('Error subscribing to barber queue:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [barberId, branchId, enabled]);

  // Subscribe to the barber's real-time status, para conocer el descanso en curso.
  useEffect(() => {
    if (!enabled || !barberId) {
      setBarberStatus(null);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'barberStatus', barberId),
      (snapshot) => {
        setBarberStatus(snapshot.exists() ? (snapshot.data() as BarberStatus) : null);
      },
      (err) => {
        // Si falla, se degrada al cálculo sin descanso en vez de romper la pantalla.
        console.error('Error subscribing to barberStatus:', err);
        setBarberStatus(null);
      }
    );

    return () => unsubscribe();
  }, [barberId, enabled]);

  // Calculate total wait time in real-time
  useEffect(() => {
    // No se sale por `tickets.length === 0`: con la cola vacía pero el barbero en
    // descanso, la espera NO es cero, es lo que le queda de descanso.
    if (!enabled) {
      setTotalWaitSeconds(0);
      return;
    }


    const calculateWaitTime = () => {
      // Only count active tickets (not completed/cancelled/expired)
      const activeTickets = tickets.filter((ticket) =>
        ['waiting', 'notified', 'arrived', 'in_service', 'finishing'].includes(ticket.status)
      );

      // El descanso pendiente va primero: bloquea al barbero antes de que pueda
      // empezar cualquier turno de los que hay por delante.
      let totalSeconds = 0;

      activeTickets.forEach((ticket) => {
        if (ticket.status === 'in_service' && ticket.timerExpiry) {
          // Service in progress - use actual time remaining from timer
          const remainingSeconds = getTimeRemaining(ticket.timerExpiry);
          totalSeconds += remainingSeconds;
        } else if (ticket.status === 'finishing' && ticket.finishingAt) {
          // Service finishing soon - calculate time until finishingAt
          const now = new Date().getTime();
          const finishingAtMs = ticket.finishingAt.toMillis();
          const remainingMs = Math.max(0, finishingAtMs - now);
          totalSeconds += Math.ceil(remainingMs / 1000);
        } else {
          // Waiting/notified/arrived - use service duration
          const durationMinutes = ticket.serviceDuration || 30;
          totalSeconds += durationMinutes * 60;
        }
      });

      // Quien pide turno ahora entra al final de la cola, y la comida está
      // anclada al final de la cola que había al marcarla: siempre va detrás.
      // Comparar minutos era el defecto (bases distintas en navegador y
      // servidor); ver useQueueEstimation. Aviso del negocio del 7-09-2026.
      totalSeconds += pendingBreakSeconds(barberStatus);

      setTotalWaitSeconds(totalSeconds);
    };

    // Initial calculation
    calculateWaitTime();

    // Update every 5 seconds for better performance
    const interval = setInterval(calculateWaitTime, 5000);

    return () => clearInterval(interval);
  }, [tickets, enabled, barberStatus]);

  // Count of waiting clients
  const waitingCount = tickets.filter((ticket) =>
    ['waiting', 'notified', 'arrived', 'in_service', 'finishing'].includes(ticket.status)
  ).length;

  return {
    totalWaitMinutes: Math.ceil(totalWaitSeconds / 60),
    totalWaitSeconds,
    waitingCount,
    loading,
    error,
  };
}
